<script setup lang="ts">
import { FrontTypeEnum, type PaymentProviderType } from "@innoclouds/api";
import { DecorIcon } from "@innoclouds/ui";
import { useTranslation } from "i18next-vue";
import { computed } from "vue";
import chevronRightIcon from "#/assets/icons/chevron_right.svg";
import HandBand from "#/assets/icons/hand_band.svg";
import PayCard from "#/assets/icons/pay_card.svg";
import YandexBadge from "#/assets/icons/yandex_badge.svg";
import ArrowLeft from "#/assets/iconsDynamic/ArrowLeft.vue";
import { useDecorationStore } from "#/store/decoration";
import AnimNum from "#/ui/recountNumber/RecountNumber.vue";
import KsoWaves from "../ksoWaves/KsoWaves.vue";

interface Props {
	providers: PaymentProviderType[];
	total: number;
	currencySymbol?: string | null;
	decoration?: string;
}

const props = withDefaults(defineProps<Props>(), { currencySymbol: null, decoration: undefined });

const emit = defineEmits<{
	select: [provider: PaymentProviderType];
	back: [];
}>();

const { t } = useTranslation();
const decor = useDecorationStore().screen(props.decoration);

const METHOD_KINDS: Record<string, string> = {
	[FrontTypeEnum.SBP]: "sbp",
	[FrontTypeEnum.CASH]: "cash",
	[FrontTypeEnum.YANDEX_BADGE]: "yandex",
	[FrontTypeEnum.PAYMENT_TRACKER]: "tracker",
};

const methodIcon = (provider: PaymentProviderType) => decor.icon(`kso_method_icon_${METHOD_KINDS[provider?.front_type ?? ""] ?? "card"}`);
const goIcon = computed(() => decor.icon("kso_method_go"));
</script>

<template>
	<div class="kso_methods" v-bind="decor.bind('kso_methods')">
		<KsoWaves v-if="decor.visible('kso_waves', false)" :decoration="props.decoration" />
		<h2 class="kso_methods_title" v-bind="decor.bind('kso_methods_title')">{{ t(`cart-payment.title`) }}</h2>
		<p class="kso_methods_subtitle" v-bind="decor.bind('kso_methods_subtitle')">{{ t(`cart-payment.description`) }}</p>

		<ul class="kso_methods_list" v-bind="decor.bind('kso_methods_list')">
			<li v-for="(provider, index) in props.providers" :key="provider?.front_type ?? index">
				<button type="button" class="kso_methods_card" v-bind="decor.bind('kso_method')" @click="emit('select', provider)">
					<span v-if="methodIcon(provider)" class="kso_methods_icon_box" v-bind="decor.bind('kso_method_icon')">
						<DecorIcon :icon="methodIcon(provider)" :fallback="PayCard" />
					</span>
					<span v-else-if="provider?.front_type === FrontTypeEnum.SBP" class="kso_methods_icon_qr" aria-hidden="true">
						<i />
						<i />
						<i />
						<span class="kso_methods_icon_qr_dots"><i /><i /><i /><i /></span>
					</span>
					<span v-else-if="provider?.front_type === FrontTypeEnum.CASH" class="kso_methods_icon_cash" aria-hidden="true">
						<i class="kso_methods_icon_cash_back" />
						<span class="kso_methods_icon_cash_front"><b>₽</b></span>
					</span>
					<img v-else-if="provider?.front_type === FrontTypeEnum.YANDEX_BADGE" class="kso_methods_icon" :src="YandexBadge" alt="">
					<img v-else-if="provider?.front_type === FrontTypeEnum.PAYMENT_TRACKER" class="kso_methods_icon" :src="HandBand" alt="">
					<img v-else class="kso_methods_icon" :src="PayCard" alt="">
					<span class="kso_methods_text" v-bind="decor.bind('kso_method_text')">
						<b v-bind="decor.bind('kso_method_name')">{{ provider?.name }}</b>
						<span v-bind="decor.bind('kso_method_description')">{{ provider?.description }}</span>
					</span>
					<span v-if="goIcon" class="kso_methods_go" v-bind="decor.bind('kso_method_go')">
						<DecorIcon :icon="goIcon" :fallback="chevronRightIcon" />
					</span>
					<ArrowLeft v-else class="kso_methods_chevron" />
				</button>
			</li>
		</ul>

		<div class="kso_methods_footer" v-bind="decor.bind('kso_methods_footer')">
			<p v-if="decor.visible('kso_methods_total')" class="kso_methods_total" v-bind="decor.bind('kso_methods_total')">
				{{ t(`cart-payment.to-be-paid`) }}
				<AnimNum :price="props.total" :symbol="props.currencySymbol" />
			</p>
			<button type="button" class="kso_methods_back" v-bind="decor.bind('kso_methods_back')" @click="emit('back')">{{ t(`cart-payment.back`) }}</button>
		</div>
	</div>
</template>

<style lang="scss" scoped>
  @use './ksoPaymentMethods.scss';
</style>
